import MessagesClient from 'flux/clients/messages';
import useReplaceUrlParam from 'hooks/useReplaceUrlParam';
import lscache from 'lscache';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTrekker } from 'utils/analytics';
import convertTo24HourFormatObject from 'utils/timeHelpers/convertTo24HourFormatObject';
import { CONTACT_HOST_SOURCES } from '~/constants';
import { RequestPersonalizedOfferType } from '~/reusable-components/forms/ContactHostForm/RequestPersonalizedOfferForm/types/types';

const url_pattern = {
  host_page: '/host/:pretty_url/contact/:form/success/',
  local_picker: '/experience/:id/locals/contact/:hostId/:form/success/',
  experience_page: '/experience/:id/contact/:hostId/:form/success/',
};

interface UseContactHostSendingProps {
  source: CONTACT_HOST_SOURCES;
}

const useContactHostSending = ({ source }: UseContactHostSendingProps) => {
  const trekker = useTrekker();
  const history = useHistory();
  const pathname = history.location.pathname;
  const replaceUrlParam = useReplaceUrlParam(url_pattern[source]);
  const [isSending, setIsSending] = useState(false);

  const onErrorRedirect = useCallback(
    (error: any) => {
      const newParams = new URLSearchParams(history.location.search);
      newParams.set(
        'error',
        error?.message ||
          error?.responseText ||
          error?.error ||
          JSON.stringify(error),
      );
      history.replace({
        pathname: pathname.replace('sending/', ''),
        search: newParams.toString(),
      });
    },
    [history, pathname],
  );

  const onPersonalizedOffer = useCallback(async () => {
    setIsSending(true);
    const formData = lscache.get('personalizeOfferForm');
    if (!formData) {
      history.replace(pathname.replace('sending/', ''));
      return;
    }

    const timeObject = convertTo24HourFormatObject(formData.time);

    const nrOfAdults = parseInt(formData.adults, 10);
    const nrOfChildren = parseInt(formData.children, 10);
    const duration = parseInt(formData.duration, 10);

    const requestWithDuration = duration
      ? `${formData.request}\nRequested duration: ${duration} hours`
      : formData.request;

    const request: RequestPersonalizedOfferType = {
      body: requestWithDuration,
      date: `${formData.date}T${timeObject.hour}:${timeObject.minute}`,
      number_of_people: nrOfAdults + nrOfChildren,
      number_of_children: nrOfChildren,
      requestType: 'NEWCUSTOMTRIP',
      to: formData.hostId,
      duration: duration * 60, // backend expects duration in minutes
      experience_id: formData.experienceId,
      source,
    };

    try {
      const response = await MessagesClient.sendOfferRequest(request);
      if (response.error) {
        return onErrorRedirect(response.error);
      }

      const trekkerContext = {
        pageName: 'contactHostFormSent',
        section: ['contactHostFormSent'],
        experience: { id: formData.experienceId },
        host: { id: formData.hostId },
        enabledForm: 'personalized-offer',
        source,
        nrOfAdults,
        nrOfChildren,
        date: formData.date,
        time: `${timeObject.hour}:${timeObject.minute}`,
        duration,
      };

      trekker.event('submit', trekkerContext);

      return replaceUrlParam('hostId', formData.hostId);
    } catch (error) {
      return onErrorRedirect(error);
    }
  }, [source, history, pathname, trekker, replaceUrlParam, onErrorRedirect]);

  const onTrigger = useCallback(() => {
    if (!isSending) {
      onPersonalizedOffer();
    }
  }, [onPersonalizedOffer, isSending]);

  useEffect(() => {
    onTrigger();
  }, [onTrigger]);

  return null;
};

export default useContactHostSending;
